import { useState } from 'react'

import { AuthForm } from './form'
import { Logo } from '../../components/logo'
import { StatusDot } from '../../components/tag'
import { T } from '../../copy/text'
import './auth.css'

/**
 * 登录页。整屏，没有侧边栏——还没登录的人不该看到导航。
 *
 * 左 46% 是深色品牌区（三条卖点 + Lulu 立绘位），右边是表单。
 * 两态（登录 / 开个账号）切换只换四句话与两个可选区块，布局不动。
 *
 * 邮箱 + 密码是默认入口；Passkey、验证码与恢复码仍是独立的备用路径。
 * 开账号会先验证邮箱，空平台首人则明确切到一次性 bootstrap 模式。
 */
export function AuthScreen() {
  const [signUp, setSignUp] = useState(false)
  // 邀请链接是 /auth?invited=1&email=…：预填邮箱，并把「你是被叫来一起管的？」那一段亮出来。
  // 链接不带秘密——身份按邮箱配，登录还得收得到那个邮箱的验证码。
  const invited = readInviteLink()

  return (
    <div className="auth-theme flex min-h-screen min-w-[1000px]">
      <Brand />
      <div className="flex min-w-[440px] flex-1 flex-col items-center justify-center gap-[24px] p-[48px]">
        {invited.invited ? (
          <div data-testid="auth-invited" className="w-full max-w-[380px] rounded-npc-card border border-npc-border-soft bg-npc-panel p-npc-card">
            <div className="text-npc-section-sm font-semibold">
              <T id="auth.invited.title" />
            </div>
            <p className="m-0 mt-[6px] text-npc-body-sm leading-[1.7] text-npc-ink-soft">
              <T id="auth.invited.body" />
            </p>
          </div>
        ) : null}
        <AuthForm signUp={signUp} onToggle={() => setSignUp((value) => !value)} initialEmail={invited.email} />
      </div>
    </div>
  )
}

/** 读邀请链接上的两样：invited=1 与 email。别的页面不认这两个参数。 */
export function readInviteLink(search: string = typeof window === 'undefined' ? '' : window.location.search): {
  invited: boolean
  email: string
} {
  const params = new URLSearchParams(search)
  return { invited: params.get('invited') === '1', email: (params.get('email') ?? '').trim().toLowerCase() }
}

function Brand() {
  return (
    <div className="flex w-[var(--container-npc-auth-brand)] shrink-0 flex-col bg-npc-dark px-[52px] py-[48px] text-npc-dark-ink">
      <div className="mb-[64px] flex items-center gap-npc-gap">
        <Logo size="auth" />
        <div>
          <div className="font-npc-display text-npc-wordmark font-semibold">
            <T id="auth.brand.wordmark" />
          </div>
          <div className="text-npc-group-label uppercase tracking-npc-caps text-npc-dark-ink-softer">
            arron &amp; co.
          </div>
        </div>
      </div>

      <h2 className="m-0 mb-npc-card whitespace-pre-line font-npc-display text-npc-hero font-semibold leading-[1.3] tracking-npc-tight">
        <T id="auth.brand.headline" />
      </h2>
      <p className="m-0 mb-[40px] max-w-[420px] text-npc-body-xl leading-[1.8] text-npc-dark-ink-soft">
        <T id="auth.brand.subhead" />
      </p>

      <div className="flex max-w-[420px] flex-col gap-npc-card">
        <Point titleId="auth.point.ssh_once.title" detailId="auth.point.ssh_once.detail" />
        <Point titleId="auth.point.autonomous.title" detailId="auth.point.autonomous.detail" />
        <Point titleId="auth.point.team.title" detailId="auth.point.team.detail" />
      </div>

      <div className="mt-[40px] flex items-end gap-npc-card">
        {/* Lulu 的立绘还没画。位置先按 190×240 占着，出图之后换成图片 */}
        <div className="flex h-[var(--container-npc-portrait-h)] w-[var(--container-npc-portrait-w)] shrink-0 items-center justify-center overflow-hidden rounded-npc-card-lg bg-npc-dark-inner p-npc-card-sm text-center text-npc-caption text-npc-dark-ink-green">
          <T id="auth.brand.portrait_placeholder" />
        </div>
        <div className="pb-npc-gap-xs">
          <div className="mb-[5px] text-npc-body font-medium">
            <T id="auth.brand.captain.name" />
          </div>
          <div className="max-w-[200px] text-npc-caption leading-[1.7] text-npc-dark-ink-soft">
            <T id="auth.brand.captain.detail" />
          </div>
        </div>
      </div>

      <div className="mt-auto border-t border-npc-dark-border-2 pt-[32px] text-npc-caption-sm leading-[1.7] text-npc-dark-ink-green">
        <T id="auth.brand.vault_note" />
      </div>
    </div>
  )
}

function Point({
  titleId,
  detailId,
}: {
  titleId: 'auth.point.ssh_once.title' | 'auth.point.autonomous.title' | 'auth.point.team.title'
  detailId: 'auth.point.ssh_once.detail' | 'auth.point.autonomous.detail' | 'auth.point.team.detail'
}) {
  return (
    <div className="flex gap-npc-grid-sm">
      <StatusDot tone="ok" className="auth-point-dot mt-npc-gap-sm" />
      <div>
        <div className="mb-[4px] text-npc-card-title font-medium">
          <T id={titleId} />
        </div>
        <div className="text-npc-body-sm leading-[1.65] text-npc-dark-ink-soft">
          <T id={detailId} />
        </div>
      </div>
    </div>
  )
}
